import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Link, useLocation } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import './Support.css';
import CustomerInfoModal from '../../components/support/CustomerInfoModal';
import ConfirmationModal from '../../components/support/ConfirmationModal';
import TranslatorInfoModal from '../../components/support/TranslatorInfoModal';

const API_URL = process.env.REACT_APP_API_URL;

const AllJobsPage = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const location = useLocation();
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 'pending_approval');
  const [searchTerm, setSearchTerm] = useState('');
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [jobToCancel, setJobToCancel] = useState(null);
  const [isTranslatorModalOpen, setIsTranslatorModalOpen] = useState(false);
  const [selectedTranslator, setSelectedTranslator] = useState(null);

  const handleTranslatorNameClick = async (translatorId) => {
    if (!translatorId) return;
    try {
      const response = await fetch(`${API_URL}/api/support/users/${translatorId}/details`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Sunucu Hatası: ${response.status}. Cevap: ${errorText}`);
      }
      const data = await response.json();
      setSelectedTranslator(data);
      setIsTranslatorModalOpen(true);
    } catch (err) {
      alert(err.message);
    }
  };

  const fetchAllJobs = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      
      const jobsResponse = await fetch(`${API_URL}/api/support/jobs/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!jobsResponse.ok) {
        throw new Error(t('error_fetch_jobs'));
      }
      
      const jobsData = await jobsResponse.json();
      setAllJobs(jobsData);

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    fetchAllJobs();
  }, [fetchAllJobs]);

  const searchedJobs = useMemo(() => {
    if (!searchTerm) return allJobs;
    const lowerCaseSearch = searchTerm.toLowerCase();
    return allJobs.filter(job =>
      job.id.toString().includes(lowerCaseSearch) ||
      (job.customer_first_name && job.customer_first_name.toLowerCase().includes(lowerCaseSearch)) ||
      (job.customer_last_name && job.customer_last_name.toLowerCase().includes(lowerCaseSearch))
    );
  }, [allJobs, searchTerm]);

  const jobsByStatus = useMemo(() => {
    const data = searchedJobs;
    return {
      quotes: data.filter(j => j.status === 'pending_quote'),
      pending: data.filter(j => j.status === 'pending_approval'),
      inProgress: data.filter(j => ['assigned', 'in_progress', 'available'].includes(j.status)),
      inReview: data.filter(j => j.status === 'in_review'),
      completed: data.filter(j => j.status === 'completed'),
      cancelled: data.filter(j => j.status === 'cancelled')
    };
  }, [searchedJobs]);

  const openCancelModal = (job) => {
    setJobToCancel(job);
    setIsCancelModalOpen(true);
  };
  
  const handleCancelJob = async () => {
    if (!jobToCancel) return;
    try {
      const response = await fetch(`${API_URL}/api/support/jobs/${jobToCancel.id}/cancel`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error(t('error_job_cancel'));
      alert(t('success_job_cancelled'));
      fetchAllJobs();
      setIsCancelModalOpen(false);
      setJobToCancel(null);
    } catch (err) {
      alert(err.message);
    }
  };

  const handleCustomerNameClick = (job) => {
    setSelectedCustomer({
      first_name: job.customer_first_name,
      last_name: job.customer_last_name,
      email: job.customer_email,
      phone_code: job.customer_phone_code,
      phone_number: job.customer_phone_number,
      total_orders: job.customer_total_orders
    });
    setIsCustomerModalOpen(true);
  };

  const filteredJobs = useMemo(() => {
    switch (activeTab) {
      case 'pending_quote': return jobsByStatus.quotes;
      case 'pending_approval': return jobsByStatus.pending;
      case 'in_progress': return jobsByStatus.inProgress;
      case 'in_review': return jobsByStatus.inReview;
      case 'completed': return jobsByStatus.completed;
      case 'cancelled': return jobsByStatus.cancelled;
      default: return [];
    }
  }, [activeTab, jobsByStatus]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return formatInTimeZone(new Date(dateString), 'Europe/Istanbul', 'dd.MM.yyyy HH:mm');
  };

  const JobStatus = ({ status }) => (
    <span className={`status-badge status-${status.toLowerCase()}`}>
      {t(`job_status_${status.toLowerCase()}`, status)}
    </span>
  );

  const renderContent = () => {
    if (loading) return <div className="table-row-message"><i className="fas fa-spinner fa-spin"></i> {t('loading_jobs')}</div>;
    if (error) return <div className="table-row-message error">{error}</div>;
    if (filteredJobs.length === 0) return <div className="table-row-message">{t('no_jobs_in_category')}</div>;

    // Tüm sekmeler için birleşik tablo görünümü
    return (
      <table className="admin-table">
        <thead>
          <tr className="table-header">
            <th className="desktop-cell">{t('job_id')}</th>
            <th className="desktop-cell">{t('customer_up')}</th>
            <th className="desktop-cell">{t('languages_up')}</th>
            <th className="desktop-cell">{t('translators_up')}</th>
            <th className="desktop-cell">{t('status')}</th>
            <th className="desktop-cell">{t('deadline')}</th>
            <th className="desktop-cell">{t('actions')}</th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.map(job => {
            let targetLangsArray = [];
            try {
                if (job.target_langs) {
                    targetLangsArray = JSON.parse(job.target_langs);
                }
            } catch (e) {
                console.error("target_langs parse edilemedi:", job.target_langs, e);
            }
            return (
              <tr key={job.id} className="table-row">
                {/* Masaüstü Görünümü */}
                <td data-label={t('job_id')} className="desktop-cell">#{job.id}</td>
                <td data-label={t('customer')} className="desktop-cell">
                  <button className="customer-name-btn" onClick={() => handleCustomerNameClick(job)}>
                    {job.customer_first_name} {job.customer_last_name}
                  </button>
                </td>
                <td data-label={t('languages')} className="desktop-cell">
                  {job.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}
                </td>
                <td data-label={t('translators')} className="desktop-cell">
                  {job.translator_id ? (
                    <button className="customer-name-btn" onClick={() => handleTranslatorNameClick(job.translator_id)}>
                      {job.translator_first_name} {job.translator_last_name}
                    </button>
                  ) : (
                    <span className="unassigned-text">{t('unassigned')}</span>
                  )}
                </td>
                <td data-label={t('status')} className="desktop-cell"><JobStatus status={job.status} /></td>
                <td data-label={t('deadline')} className="desktop-cell">{formatDate(job.deadline)}</td>
                <td data-label={t('actions')} className="desktop-cell">
                  <Link to={`/management/jobs/${job.id}`} state={{ activeTab }} className="action-btn view">
                    <i className="fas fa-eye"></i> {t('view')}
                  </Link>
                </td>

                {/* Mobil Görünüm */}
                <td className="cell-mobile">
                  <div className="mobile-card-header">
                    <div className="mobile-card-title">
                      <h3>
                        #{job.id} - {job.source_lang ? job.source_lang.toUpperCase() : t('unknown_language')} → {(targetLangsArray && targetLangsArray.length > 0) ? targetLangsArray.join(', ').toUpperCase() : ''}
                      </h3>
                      <button className="customer-name-btn" onClick={() => handleCustomerNameClick(job)}>
                        {job.customer_first_name} {job.customer_last_name}
                      </button>
                    </div>
                    <JobStatus status={job.status} />
                  </div>
                  <div className="mobile-card-body">
                    <div className="mobile-card-line">
                      <span className="mobile-card-label">{t('translator')}</span>
                      <span className="mobile-card-value">
                        {job.translator_id ? `${job.translator_first_name} ${job.translator_last_name}` : <span className="unassigned-text">{t('unassigned')}</span>}
                      </span>
                    </div>
                    <div className="mobile-card-line">
                      <span className="mobile-card-label">{t('deadline')}</span>
                      <span className="mobile-card-value">{formatDate(job.deadline)}</span>
                    </div>
                  </div>
                  <div className="mobile-card-footer">
                    <Link to={`/management/jobs/${job.id}`} state={{ activeTab }} className="action-btn view">
                      <i className="fas fa-eye"></i> {t('view_details')}
                    </Link>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  return (
    <>
      <div className="page-container admin-dashboard">
        <div className="support-page-header">
          <h1 className="support-page-title">{t('all_jobs_title')}</h1>
          <div className="support-filters-compact">
            <input type="text" placeholder={t('filter_by_id_or_name')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
          </div>
        </div>
        <div className="account-content-card">
          <div className="status-tabs support-tabs">
            <button onClick={() => setActiveTab('pending_quote')} className={activeTab === 'pending_quote' ? 'active' : ''}>
              {t('quotes', 'Teklifler')} ({jobsByStatus.quotes.length})
            </button>
            <button onClick={() => setActiveTab('pending_approval')} className={activeTab === 'pending_approval' ? 'active' : ''}>
              {t('pending_approval')} ({jobsByStatus.pending.length})
            </button>
            <button onClick={() => setActiveTab('in_progress')} className={activeTab === 'in_progress' ? 'active' : ''}>
              {t('in_progress')} ({jobsByStatus.inProgress.length})
            </button>
            <button onClick={() => setActiveTab('in_review')} className={activeTab === 'in_review' ? 'active' : ''}>
              {t('in_review')} ({jobsByStatus.inReview.length})
            </button>
            <button onClick={() => setActiveTab('completed')} className={activeTab === 'completed' ? 'active' : ''}>
              {t('completed')} ({jobsByStatus.completed.length})
            </button>
            <button onClick={() => setActiveTab('cancelled')} className={activeTab === 'cancelled' ? 'active' : ''}>
              {t('cancelled')} ({jobsByStatus.cancelled.length})
            </button>
          </div>
          {renderContent()}
        </div>
      </div>
      <CustomerInfoModal isOpen={isCustomerModalOpen} onClose={() => setIsCustomerModalOpen(false)} customer={selectedCustomer} />
      <ConfirmationModal 
        isOpen={isCancelModalOpen} 
        onClose={() => setIsCancelModalOpen(false)} 
        onConfirm={handleCancelJob} 
        title={t('confirm_cancel_job_title')} 
        message={t('confirm_cancel_job_message', { jobId: jobToCancel?.id })} 
      />
      <TranslatorInfoModal
        isOpen={isTranslatorModalOpen}
        onClose={() => setIsTranslatorModalOpen(false)}
        translator={selectedTranslator}
      />
    </>
  );
};

export default AllJobsPage;